// Catalog.ID Client — Devices Pane
// ============================================================================
// Lists all registered devices. Manage current device (change PIN, remove).
// Revoke access to other devices by deleting them by ID.

import { createElement, createFieldGroup, createFormRow, createEmptyState } from '@catalog-shell';
import type { PaneType, PaneTypeRenderContext } from '@catalog-shell';
import { renderDeviceSetup, encryptLabelForDevice, applyLabelToKnownDevice } from '../components/device-setup';
import { decryptDeviceLabel, MAX_DEVICE_LABEL_CHARS } from '../crypto/device-label';
import * as deviceApi from '../api/device';
import * as accountApi from '../api/account';
import * as aes from '../crypto/aes';
import * as deviceStorage from '../state/device-storage';
import { keys } from '../state/keys';
import { getSlotKey } from '../state/device-slots';
import { readDeviceKeyManifest, writeDeviceKeyManifest, type DeviceKeyGroup } from '../state/device-key-selection';
import { readKeyUseRecord } from '../state/key-use-record';
import { requireKey } from '../key-grant/on-demand-key';
import {
    COPIED_KEY_NOTE, SUPERSECRET_ONLY_NOTE, WALLET_DOCUMENT_NOTE, buildDeviceKeyRows,
    canKeepKeysOnDevice, deviceKeyActionLabel, deviceKeyStateLabel, tickDeviceKey,
    untickDeviceKey, type DeviceKeyActionDeps, type DeviceKeyRow,
} from './device-keys-core';
import { signStepUp } from '../auth/step-up';
import { confirmAndChangePin } from '../components/device-mode-confirm';
import { removeCurrentDeviceAndSignOut } from '../auth/device-session';
import { buildSessionDeps } from '../auth/logout-action';
import type { DeviceInfo } from '../api/types';
import { PANE_IDS } from '../shell-config';

/**
 * A device's mode, as the member reads it (device-setup-restructure §6.4). The
 * node stores two orthogonal columns — `accessmode` (`open` | `pin`) and
 * `totprequired` — and the four modes are their product, so the label is a
 * function of the pair rather than a lookup on either.
 */
export function deviceModeLabel(accessMode: string, totpRequired: boolean): string {
    if (accessMode === 'pin') return totpRequired ? 'PIN + Authenticator' : 'PIN';
    if (accessMode === 'open') return totpRequired ? 'Authenticator' : 'No PIN';
    // An unknown mode is shown verbatim rather than guessed at; the factor is
    // still worth naming, because it is what the member will be asked for.
    return totpRequired ? `${accessMode} + Authenticator` : accessMode;
}

interface DeviceTitle {
    /** What to show as the row title. */
    display: string;
    /** The decrypted name, or '' when there is none to prefill a rename with. */
    plain: string;
    /** True when a name IS on record but this session cannot read it. */
    unreadable: boolean;
}

/**
 * Resolve a device's display title. Names are encrypted to the account's
 * attr_encrypt key (crypto/device-label.ts) and the record names the keyIndex
 * used, so a name written before a key rotation still opens with the older key
 * from the chain. Anything unreadable degrades to "Device #N" — a device row
 * that can't render its name must still render its actions.
 */
async function resolveDeviceTitle(device: DeviceInfo): Promise<DeviceTitle> {
    const fallback = `Device #${device.deviceId}`;
    if (!device.label || device.labelKeyIndex === null || device.labelKeyIndex === undefined) {
        return { display: fallback, plain: '', unreadable: false };
    }
    const entry = keys.getKeyChain('attr_encrypt').find(k => k.keyIndex === device.labelKeyIndex);
    if (!entry) return { display: fallback, plain: '', unreadable: true };
    try {
        const plain = await decryptDeviceLabel(device.label, entry.secretKey);
        return { display: plain, plain, unreadable: false };
    } catch {
        return { display: fallback, plain: '', unreadable: true };
    }
}

export function createDevicesPane(): PaneType {
    return {
        typeId: PANE_IDS.devices,
        kind: 'leaf',
        defaultTitle: 'Devices',
        capabilities: [],
        render: (ctx: PaneTypeRenderContext) => {
            const container = createElement('div', { className: 'cid-pane-content' });
            void renderDevices(container, ctx);
            return container;
        },
    };
}

async function renderDevices(container: HTMLElement, ctx: PaneTypeRenderContext): Promise<void> {
    const cid = deviceStorage.getActiveCid();
    if (!cid) return;
    const currentToken = deviceStorage.getDeviceToken(cid);
    // The server never returns device tokens, so the row for THIS browser is
    // identified by the device id captured from its own setup/reset response.
    const currentDeviceId = deviceStorage.getDeviceId(cid);

    // Load all devices from server
    let devices: DeviceInfo[] = [];
    try {
        devices = await deviceApi.listDevices();
    } catch {
        container.appendChild(createEmptyState({
            message: 'Failed to Load Devices',
            description: 'Could not retrieve device list from server.',
        }));
        return;
    }

    if (devices.length === 0) {
        const group = createFieldGroup({ title: 'Devices' });
        group.body.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'No devices registered. Your keys are loaded from your account each session (supersecret required).',
        }));
        container.appendChild(group.element);
        return;
    }

    // This browser's row is identified by `currentDeviceId` (captured from its
    // own setup/reset response); the server never returns device tokens, so the
    // token alone can only say "we have one", not "this is it".

    const devicesGroup = createFieldGroup({ title: `Devices (${devices.length})` });

    // The key ids the SERVER bag holds, for the "Keys on this device" group
    // (device-setup-restructure §9.3): a row is "new" only when the bag
    // actually holds the key. Ids only — the entries stay encrypted and none
    // of them is decrypted here. A failed read costs the "new" marking and
    // nothing else, so it is not worth failing the pane over.
    let serverKeyIds: string[] = [];
    if (currentToken && currentDeviceId !== null) {
        try {
            serverKeyIds = (await accountApi.getKeyBag()).entries.map(e => e.keyId);
        } catch { /* see above — no "new" rows, everything else still renders */ }
    }
    const reloadDevices = (): void => {
        container.innerHTML = '';
        void renderDevices(container, ctx);
    };

    for (const device of devices) {
        const row = createElement('div', { className: 'cid-share-item' });
        const title = await resolveDeviceTitle(device);

        const infoCol = createElement('div');
        infoCol.appendChild(createElement('span', {
            className: 'cid-profile-cid',
            textContent: title.display + (device.deviceId === currentDeviceId ? ' — this device' : ''),
        }));
        if (title.unreadable) {
            infoCol.appendChild(createElement('span', {
                className: 'cid-pane-note',
                textContent: ' (name unreadable)',
            }));
        }
        infoCol.appendChild(createElement('span', {
            className: 'cid-wizard-text',
            textContent: ` — ${deviceModeLabel(device.accessMode, device.totpRequired)}` +
                (device.isLocked ? ' (LOCKED)' : '') +
                ` — last used ${new Date(device.lastUsedAt).toLocaleDateString()}`,
        }));
        row.appendChild(infoCol);

        const renameBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small',
            textContent: 'Rename',
        });
        renameBtn.addEventListener('click', () => {
            showRenameDeviceModal(ctx, cid, device, title.plain, () => {
                container.innerHTML = '';
                void renderDevices(container, ctx);
            });
        });
        row.appendChild(renameBtn);

        const revokeBtn = createElement('button', {
            className: 'cs-btn cs-btn--no cs-btn--small',
            textContent: 'Remove',
        });
        const isCurrentDevice = device.deviceId === currentDeviceId;
        revokeBtn.addEventListener('click', async () => {
            const ok = await ctx.shell.confirm({
                title: 'Remove Device',
                // §15 B: removing THIS browser's device ends the session too, so
                // the confirm has to say so — "Remove" here is a sign-out.
                message: isCurrentDevice
                    ? `Remove ${title.display}? Its stored keys become unusable and you will be `
                        + 'signed out of this browser. Signing in again needs your password and '
                        + 'your supersecret.'
                    : `Remove ${title.display}? Its stored keys will become unusable.`,
                confirmLabel: 'Remove',
                confirmVariant: 'no',
            });
            if (!ok) return;

            revokeBtn.setAttribute('disabled', '');
            try {
                if (isCurrentDevice) {
                    // Deleting the row destroys the key that opens this
                    // browser's copy of the bag — but the bag is still
                    // DECRYPTED in memory and the session token is still in
                    // localStorage, so until 2026-09-18 a walk-up could set the
                    // device straight back up in any mode they liked. That is
                    // the bypass the downgrade gate (§15 A) would otherwise
                    // leave open, because `POST /api/device/setup` is
                    // deliberately ungated. Sign the browser out instead.
                    await removeCurrentDeviceAndSignOut({
                        ...buildSessionDeps(),
                        removeCurrentDeviceRecord: () => deviceApi.removeDeviceById(device.deviceId),
                    }, cid);
                    window.location.href = '/';
                    return;
                }
                await deviceApi.removeDeviceById(device.deviceId);
                ctx.shell.toast(`Device #${device.deviceId} removed`, 'success');
                row.remove();

                if (currentToken && currentDeviceId === null) {
                    // Pre-device-id local state: we can't tell which row was ours,
                    // so fall back to "nothing left ⇒ nothing of ours survives".
                    const remaining = devicesGroup.body.querySelectorAll('.cid-share-item');
                    if (remaining.length === 0) {
                        deviceStorage.clearDeviceStorage(cid);
                    }
                }
            } catch (err) {
                revokeBtn.removeAttribute('disabled');
                ctx.shell.toast(err instanceof Error ? err.message : 'Failed to remove device', 'error');
            }
        });
        row.appendChild(revokeBtn);

        devicesGroup.body.appendChild(row);

        // §9.3: the key group belongs under THIS device's row and nowhere
        // else. What another device holds is that device's business — the
        // choice is local to each browser and the server never learns it
        // (§7.1), so there is nothing here to show for the other rows.
        if (currentToken && device.deviceId === currentDeviceId) {
            devicesGroup.body.appendChild(
                renderDeviceKeysGroup(ctx, cid, serverKeyIds, reloadDevices));
        }
    }

    container.appendChild(devicesGroup.element);

    // Current device management
    if (currentToken) {
        const currentGroup = createFieldGroup({ title: 'This Device' });

        const hasKeys = deviceStorage.hasStoredKeys(cid);
        const modeRow = createFormRow({ label: 'Local Keys' });
        modeRow.content.appendChild(createElement('span', {
            textContent: hasKeys ? 'Stored (encrypted)' : 'None',
        }));
        currentGroup.body.appendChild(modeRow.element);

        const actionsRow = createElement('div', { className: 'cid-dashboard-actions' });

        const changePinBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small',
            textContent: 'Change PIN / Mode',
        });
        changePinBtn.addEventListener('click', () => {
            void showChangePinFlow(container, ctx,
                devices.find(d => d.deviceId === currentDeviceId) ?? null);
        });
        actionsRow.appendChild(changePinBtn);

        currentGroup.body.appendChild(actionsRow);
        container.appendChild(currentGroup.element);
    } else {
        // No device token — "no local storage" mode or new device.
        // Show option to set up local key storage.
        const setupGroup = createFieldGroup({ title: 'This Device' });

        const modeRow = createFormRow({ label: 'Mode' });
        modeRow.content.appendChild(createElement('span', {
            textContent: 'No local storage — enter supersecret each session',
        }));
        setupGroup.body.appendChild(modeRow.element);

        const actionsRow = createElement('div', { className: 'cid-dashboard-actions' });
        const setupBtn = createElement('button', {
            className: 'cs-btn cs-btn--default cs-btn--small',
            textContent: 'Set Up Local Storage',
        });
        setupBtn.addEventListener('click', () => {
            // No device token: there is no row to downgrade, so no factor to
            // present — this path always goes through `setupDevice`.
            void showChangePinFlow(container, ctx, null);
        });
        actionsRow.appendChild(setupBtn);

        setupGroup.body.appendChild(actionsRow);
        container.appendChild(setupGroup.element);
    }

    // Info
    const infoGroup = createFieldGroup({ title: 'About Devices' });
    infoGroup.body.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: 'Each device stores encrypted copies of your private keys in the browser\'s localStorage. '
            + 'The encryption key is held on the server and released only after PIN verification. '
            + 'Removing a device deletes the server\'s copy of its encryption key, making its stored keys unusable.',
    }));
    container.appendChild(infoGroup.element);
}

// ============================================================================
// "Keys on this device" (device-setup-restructure §9.3)
// ============================================================================
// Whitepaper §3.4.7 made visible: the member sees which of their keys THIS
// browser holds, what it has done with each of them, and can change the answer
// at any time. Every decision below is made in `device-keys-core.ts`, which is
// DOM-free and tested; this half only draws it.
//
// The asymmetry is deliberate and the copy says so. Unticking is free and
// immediate — the key bag still holds the key, and the manifest entry stops the
// reconcile from calling the copy stale. Ticking costs a supersecret, because
// the key has to be decrypted out of the bag before it can be re-encrypted
// under this device's key; in a session that unlocked with the supersecret
// alone there is no device key to encrypt under, so the action is disabled with
// a note instead of failing halfway.

/** The live wiring of the core's injected deps. Nothing here decides
 *  anything — see `device-keys-core.ts`. */
function deviceKeyActionDeps(
    ctx: PaneTypeRenderContext, cid: string, serverKeyIds: readonly string[],
): DeviceKeyActionDeps {
    return {
        cid,
        shell: ctx.shell,
        serverKeyIds,
        requireKey,
        getSlotKey,
        removeStoredKeyEntries: deviceStorage.removeStoredKeyEntries,
        listStoredEntries: (account) => deviceStorage.getStoredKeyEntries(account),
        storeEntry: (account, entry, dek) => deviceStorage.encryptAndStoreKey(
            account, entry.keyId, aes.fromBase64(entry.privateKey), dek,
            {
                algorithm: entry.algorithm,
                keyIndex: entry.keyIndex ?? 0,
                status: entry.status ?? 'active',
            }),
        readManifest: readDeviceKeyManifest,
        writeManifest: writeDeviceKeyManifest,
    };
}

function renderDeviceKeysGroup(
    ctx: PaneTypeRenderContext,
    cid: string,
    serverKeyIds: readonly string[],
    reload: () => void,
): HTMLElement {
    const wrap = createElement('div', {
        className: 'cid-device-keys',
        style: { margin: '4px 0 16px 16px' },
    });
    wrap.appendChild(createElement('div', {
        className: 'cid-profile-cid',
        textContent: 'Keys on this device',
    }));
    wrap.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Only this browser knows which of your keys it keeps — the choice is never '
            + 'sent to the server. A key you remove stays on your account; loading it here again '
            + 'asks for your supersecret.',
    }));

    const deps = deviceKeyActionDeps(ctx, cid, serverKeyIds);
    const canKeep = canKeepKeysOnDevice(deps);
    if (!canKeep) {
        wrap.appendChild(createElement('p', {
            className: 'cid-pane-note',
            textContent: SUPERSECRET_ONLY_NOTE,
        }));
    }

    const rows = buildDeviceKeyRows({
        storedKeyIds: deviceStorage.getStoredKeyIds(cid),
        serverKeyIds,
        manifest: readDeviceKeyManifest(cid),
        useRecord: readKeyUseRecord(cid),
    });

    let group: DeviceKeyGroup | null = null;
    for (const row of rows) {
        if (row.group !== group) {
            group = row.group;
            wrap.appendChild(createElement('div', {
                className: 'cid-wizard-text',
                textContent: group,
                style: { marginTop: '10px', fontWeight: '600' },
            }));
        }
        wrap.appendChild(renderDeviceKeyRow(ctx, row, deps, canKeep, reload));
    }

    // §9.3's last sentence: the wallet DOCUMENT is handed over as a copy too,
    // and it is not a key, so it is not a row here. The member still has to be
    // told that removing it somewhere would not take the copy back.
    wrap.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: WALLET_DOCUMENT_NOTE,
        style: { marginTop: '10px' },
    }));
    return wrap;
}

function renderDeviceKeyRow(
    ctx: PaneTypeRenderContext,
    row: DeviceKeyRow,
    deps: DeviceKeyActionDeps,
    canKeep: boolean,
    reload: () => void,
): HTMLElement {
    const el = createElement('div', { className: 'cid-share-item' });

    const infoCol = createElement('div');
    infoCol.appendChild(createElement('span', {
        className: 'cid-profile-cid',
        textContent: row.label,
    }));
    infoCol.appendChild(createElement('span', {
        className: 'cid-wizard-text',
        textContent: ` — ${deviceKeyStateLabel(row.state)}`,
    }));
    infoCol.appendChild(createElement('div', {
        className: 'cid-pane-note',
        textContent: row.description,
    }));
    if (!row.mandatory) {
        // §9.4's evidence. Not shown for the mandatory rows: there is nothing
        // to decide about them, and `recordKeyUse` refuses to write them.
        infoCol.appendChild(createElement('div', {
            className: 'cid-pane-note',
            textContent: row.useLine,
        }));
    }
    if (row.copied) {
        infoCol.appendChild(createElement('div', {
            className: 'cid-pane-note',
            textContent: COPIED_KEY_NOTE,
        }));
    }
    el.appendChild(infoCol);

    if (row.mandatory) {
        // No toggle at all, rather than a disabled one: the three Core keys are
        // not a choice the member is being denied, they are what a device that
        // stores keys at all holds (§1 item 10).
        el.appendChild(createElement('span', {
            className: 'cid-wizard-text',
            textContent: '(required)',
        }));
        return el;
    }

    const onDevice = row.state === 'on-device';
    const btn = createElement('button', {
        className: onDevice
            ? 'cs-btn cs-btn--no cs-btn--small'
            : 'cs-btn cs-btn--default cs-btn--small',
        textContent: deviceKeyActionLabel(row.state),
    });
    if (!onDevice && !canKeep) btn.setAttribute('disabled', '');
    btn.addEventListener('click', async () => {
        btn.setAttribute('disabled', '');
        try {
            if (onDevice) {
                untickDeviceKey(row.id, deps);
                ctx.shell.toast(`${row.label} removed from this device`, 'success');
                reload();
                return;
            }
            const outcome = await tickDeviceKey(row.id, deps);
            if (outcome.status === 'kept') {
                ctx.shell.toast(`${row.label} is kept on this device`, 'success');
                reload();
                return;
            }
            // Nothing changed — say why on the spot and leave the row as it was.
            if (outcome.status === 'unchanged') ctx.shell.toast(outcome.note, 'info');
            btn.removeAttribute('disabled');
        } catch (err) {
            ctx.shell.toast(
                err instanceof Error ? err.message : 'Could not change this key', 'error');
            btn.removeAttribute('disabled');
        }
    });
    el.appendChild(btn);
    return el;
}

/**
 * Change this browser's device mode (§6.3), or set one up when there is none.
 *
 * `currentDevice` is this browser's row as the node lists it, or null when
 * there is none. It is what the downgrade gate (§15 A) is computed from: a
 * change that REMOVES a factor — the PIN, or the Authenticator code — has to
 * present that factor before the node will write it, and the confirm step in
 * `components/device-mode-confirm.ts` is where the member does so. The row's
 * PIN LENGTH is not on the wire (the device list never carries it), so it comes
 * from this browser's own record.
 */
async function showChangePinFlow(
    container: HTMLElement, ctx: PaneTypeRenderContext, currentDevice: DeviceInfo | null,
): Promise<void> {
    const cid = deviceStorage.getActiveCid();
    if (!cid) return;
    const existingToken = deviceStorage.getDeviceToken(cid);
    const isNewDevice = !existingToken;

    // Build a KeyBag from the currently loaded keys (needed by renderDeviceSetup for localStorage encryption)
    const bag = keys.getKeyBag();
    if (!bag || bag.entries.length === 0) {
        ctx.shell.toast('Keys not loaded — cannot change device mode', 'error');
        return;
    }

    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: isNewDevice ? 'Set Up Device' : 'Change PIN / Access Mode',
    }));

    const modal = ctx.shell.showModal(content);

    renderDeviceSetup(content, bag, cid, {
        onSetup: async (params: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[0]) => {
            // `label` is plaintext — encrypt it here; never forward it as-is.
            const { label: plainLabel, ...rest } = params;
            const labelFields = await encryptLabelForDevice(cid, plainLabel);
            if (isNewDevice) {
                // New device — create via setupDevice
                const resp = await deviceApi.setupDevice({ ...rest, ...labelFields });
                deviceStorage.setDeviceId(cid, resp.deviceId);
            } else {
                // Existing device — update via changePin with new DEK. Step-up
                // gated: rewriting a device's guard needs proof of
                // main_signing_key possession, not just the session token. The
                // bag is loaded (checked above), so signing is available here.
                //
                // The downgrade gate (§15 A): a change that takes a factor away
                // presents it first. `confirmAndChangePin` computes what is
                // owed from the row, collects only what is not already in hand
                // — the mode step's live code counts — and mints a FRESH
                // challenge per attempt, because the challenge is single use.
                await confirmAndChangePin({
                    host: content,
                    current: {
                        accessMode: currentDevice?.accessMode ?? 'open',
                        totpRequired: currentDevice?.totpRequired ?? false,
                        pinLength: deviceStorage.getPinLength(cid),
                    },
                    request: {
                        deviceToken: existingToken,
                        accessMode: params.accessMode,
                        pin: params.pin,
                        pinLength: params.pinLength,
                        deviceEncryptionKey: params.deviceEncryptionKey,
                        // The Authenticator factor rides the same write, with the
                        // live code that proves the member holds the key (§3).
                        totpRequired: params.totpRequired,
                        code: params.code,
                    },
                    signStepUp: () => signStepUp('device-manage'),
                    changePin: deviceApi.changePin,
                });
                // changePin carries no label — rename separately.
                await applyLabelToKnownDevice(cid, labelFields);
            }
            deviceStorage.clearNoLocalStorageMode(cid);
            modal.close();
            ctx.shell.toast('Device access mode updated', 'success');
            container.innerHTML = '';
            void renderDevices(container, ctx);
        },
        onSkip: async () => {
            // "No local storage" de-enrols this browser: the server row goes,
            // and with it the key that opens the local copy. That is the same
            // destruction as the row's own Remove button, so it ends the
            // session the same way (§15 B) — leaving the tab alive with the
            // decrypted bag in memory would let a walk-up re-enrol it in any
            // mode, which is exactly what the downgrade gate forbids.
            if (existingToken) {
                await removeCurrentDeviceAndSignOut({
                    ...buildSessionDeps(),
                    removeCurrentDeviceRecord: async () => {
                        // Best effort: the member has ASKED to stop storing keys
                        // here, so a server that cannot be reached must not
                        // leave them enrolled locally either.
                        try { await deviceApi.removeDevice({ deviceToken: existingToken }); } catch { /* */ }
                    },
                }, cid);
                // After `clearDeviceStorage`, which wipes the flag along with
                // everything else — set it so the next login here does not
                // offer device setup again.
                deviceStorage.setNoLocalStorageMode(cid);
                modal.close();
                window.location.href = '/';
                return;
            }
            deviceStorage.clearDeviceStorage(cid);
            deviceStorage.setNoLocalStorageMode(cid);
            modal.close();
            ctx.shell.toast('Device access mode updated — using supersecret each session', 'success');
            container.innerHTML = '';
            void renderDevices(container, ctx);
        },
    });
}

/**
 * Rename one device. Unlike device setup — where a failed label must never
 * block the user from configuring their keys — this IS the requested action,
 * so a failure to encrypt or store the name is reported rather than swallowed.
 * Always encrypts to the CURRENT attr_encrypt key; older labels keep their own
 * keyIndex until they are rewritten here.
 */
function showRenameDeviceModal(
    ctx: PaneTypeRenderContext,
    cid: string,
    device: DeviceInfo,
    currentName: string,
    onRenamed: () => void,
): void {
    const content = createElement('div', { className: 'cid-wizard-step' });
    content.appendChild(createElement('h3', {
        className: 'cid-wizard-heading',
        textContent: `Rename Device #${device.deviceId}`,
    }));
    content.appendChild(createElement('p', {
        className: 'cid-pane-note',
        textContent: 'Only you can read this — the name is encrypted with your own key before it is stored.',
    }));

    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cid-auth-input';
    input.style.width = '100%';
    input.maxLength = MAX_DEVICE_LABEL_CHARS;
    input.autocomplete = 'off';
    input.value = currentName;
    content.appendChild(input);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    content.appendChild(errorEl);

    const fail = (message: string): void => {
        errorEl.textContent = message;
        errorEl.classList.add('cid-pane-error--visible');
        saveBtn.removeAttribute('disabled');
        saveBtn.textContent = 'Save';
    };

    const actions = createElement('div', { className: 'cid-dashboard-actions' });
    const saveBtn = createElement('button', { className: 'cs-btn cs-btn--yes cs-btn--small', textContent: 'Save' });
    saveBtn.addEventListener('click', async () => {
        const name = input.value.trim();
        if (!name) {
            fail('Enter a device name.');
            return;
        }
        saveBtn.setAttribute('disabled', '');
        saveBtn.textContent = 'Saving...';
        errorEl.classList.remove('cid-pane-error--visible');

        const fields = await encryptLabelForDevice(cid, name);
        if (fields.label === undefined || fields.labelKeyIndex === undefined) {
            fail('Could not encrypt the name — your account keys are not available in this session.');
            return;
        }
        try {
            await deviceApi.updateDeviceLabel(device.deviceId, {
                label: fields.label,
                labelKeyIndex: fields.labelKeyIndex,
            });
            modal.close();
            ctx.shell.toast('Device renamed', 'success');
            onRenamed();
        } catch (err) {
            fail(err instanceof Error ? err.message : 'Could not rename the device');
        }
    });
    actions.appendChild(saveBtn);

    const cancelBtn = createElement('button', { className: 'cs-btn cs-btn--default cs-btn--small', textContent: 'Cancel' });
    cancelBtn.addEventListener('click', () => modal.close());
    actions.appendChild(cancelBtn);

    content.appendChild(actions);

    const modal = ctx.shell.showModal(content);
    input.focus();
    input.select();
}
